"use client";

import React from "react";
import { ChartBar, Target, Globe, Zap, ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const categories = [{
  id: "analysis",
  name: "Market Analysis",
  description: "Deep-dive intelligence into consumer behavior and competitive landscapes to find your edge.",
  icon: ChartBar,
  features: ["Data-Driven Insights", "Competitor Audits", "Trend Forecasting"],
  number: "01"
}, {
  id: "positioning",
  name: "Brand Positioning",
  description: "Architecting a unique identity that resonates with authority in high-stakes markets.",
  icon: Target,
  features: ["Identity Strategy", "Value Proposition", "Narrative Design"],
  number: "02"
}, {
  id: "advertising",
  name: "Digital Advertising",
  description: "Precision-targeted campaigns engineered for maximum conversion and brand visibility.",
  icon: Globe,
  features: ["Multi-Channel Reach", "ROI Optimization", "Creative Execution"],
  number: "03"
}, {
  id: "strategy",
  name: "Strategic Planning",
  description: "Comprehensive roadmaps designed to scale operations and outperform industry benchmarks.",
  icon: Zap,
  features: ["Growth Roadmaps", "Media Planning", "Performance Metrics"],
  number: "04"
}];
export default function RideCategories() {
  return <section data-section-id="224776" id="strategic-pillars" className="py-20 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:text-left max-w-2xl">
          <h2 className="font-default text-3xl md:text-5xl font-extrabold tracking-tighter mb-4 uppercase">
            Strategic <span className="text-primary">Pillars</span>
          </h2>
          <p className="text-muted-foreground text-lg font-default leading-tight">
            Our architectural approach to advertising combines brutalist precision with elite market intelligence to elevate your brand presence.
          </p>
        </div>

        <ScrollArea className="w-full pb-6 -mx-4 px-4 md:mx-0 md:px-0 md:pb-0">
          <div className="flex w-max space-x-4 md:w-auto md:grid md:grid-cols-2 lg:grid-cols-4 md:space-x-0 md:gap-6">
            {categories.map((category, i) => <div key={category.id} data-index={i} className="w-[280px] md:w-auto shrink-0">
                <Card className="h-full flex flex-col group relative overflow-hidden border-white/10 hover:border-primary/50 transition-all duration-500 hover:shadow-3xl hover:shadow-primary/20 bg-white/5 backdrop-blur-md rounded-lg">
                  {}
                  <div className="absolute top-4 right-4 font-mono text-sm font-bold text-primary/40 group-hover:text-primary transition-colors duration-500">
                    {category.number}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  
                  <CardHeader>
                    <div className="mb-4 relative inline-flex">
                      {}
                      <div className="absolute inset-0 bg-primary/40 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <div className="relative bg-secondary/50 p-4 rounded-sm group-hover:bg-primary/20 transition-colors duration-500 border border-white/5 group-hover:border-primary/40">
                        <category.icon className="w-12 h-12 text-primary transition-transform duration-500 group-hover:scale-110" />
                      </div>
                    </div>
                    <CardTitle className="font-default text-2xl font-bold tracking-tighter uppercase">{category.name}</CardTitle>
                    <CardDescription className="text-base font-default mt-2 leading-tight text-muted-foreground">
                      {category.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    <ul className="space-y-2.5">
                      {category.features.map((feature, idx) => <li key={idx} data-index={idx} className="flex items-center text-sm text-foreground font-mono">
                          <div className="w-1 h-1 bg-primary mr-3 shadow-[0_0_10px_var(--color-primary)]" />
                          {feature}
                        </li>)}
                    </ul>
                  </CardContent>
                  
                  <CardFooter className="pt-6">
                    <Button className="w-full group/btn rounded-sm border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300" variant="outline" asChild>
                      <Link to="/SolutionsPage">
                        Explore {category.name}
                        <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>)}
          </div>
          <ScrollBar orientation="horizontal" className="md:hidden" />
        </ScrollArea>
      </div>
    </section>;
}